/**
 * TrialBanner — banner de trial ativo exibido no topo das páginas do tenant
 * Mostra dias restantes e CTA para upgrade
 */

import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Clock, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { theme } from '../styles/theme';

export default function TrialBanner({ tenant }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const daysLeft = useMemo(() => {
    if (!tenant?.trialEndsAt || tenant?.status !== 'trial') return null;
    const diff = new Date(tenant.trialEndsAt) - new Date();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }, [tenant]);

  if (daysLeft === null) return null;

  const urgent = daysLeft <= 2;

  return (
    <Banner $urgent={urgent}>
      <BannerLeft>
        <Clock size={15} />
        <span>
          {daysLeft === 0
            ? t('trial.expirestoday')
            : t('trial.daysLeft', { count: daysLeft })}
          {tenant?.pendingTier && (
            <Muted> · {t('trial.willUpgrade', { tier: tenant.pendingTier })}</Muted>
          )}
        </span>
      </BannerLeft>
      <UpgradeBtn onClick={() => navigate(`/billing/${tenant.tenantId}`)}>
        {t('trial.upgrade')} <ArrowRight size={13} />
      </UpgradeBtn>
    </Banner>
  );
}

const Banner = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: ${theme.spacing[2]} ${theme.spacing[6]};
  background: ${p => p.$urgent ? theme.colors.dangerLight : theme.colors.warningLight};
  border-bottom: 1px solid ${p => p.$urgent ? theme.colors.dangerBorder : theme.colors.warningBorder};
  color: ${p => p.$urgent ? theme.colors.danger : theme.colors.textSecondary};
  font-size: ${theme.typography.sm};
  gap: ${theme.spacing[4]};
  flex-wrap: wrap;
`;

const BannerLeft = styled.div`
  display: flex; align-items: center; gap: ${theme.spacing[2]};
`;

const Muted = styled.span`opacity: 0.7;`;

const UpgradeBtn = styled.button`
  display: flex; align-items: center; gap: ${theme.spacing[1]};
  background: none; border: none; cursor: pointer;
  font-size: ${theme.typography.xs}; font-weight: ${theme.typography.semibold};
  color: inherit; text-decoration: underline;
  &:hover { opacity: 0.8; }
`;
